import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import {
  disconnectThreads,
  getThreadsConnection,
  startThreadsAuth,
  ThreadsConnection,
} from "../../../../database/community/threadsConnection";

const Button = styled.button`
  appearance: none;
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  padding: 4px 10px;
  border-radius: var(--radius-pill);
  cursor: pointer;
  &:hover:not(:disabled) {
    border-color: var(--color-accent);
    color: var(--color-accent);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background: var(--color-text);
  border-color: var(--color-text);
  color: #fff;
  &:hover:not(:disabled) {
    color: #fff;
    filter: brightness(1.05);
  }
`;

const PanelRoot = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  margin-bottom: var(--space-3);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
`;

const Label = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: var(--radius-pill);
  background: var(--color-surface-subtle);
  font-family: var(--font-sans);
  font-size: 11px;
  color: var(--color-text-secondary);
  img {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    object-fit: cover;
  }
`;

const Muted = styled.span`
  font-family: var(--font-sans);
  font-size: 11px;
  color: var(--color-text-secondary);
`;

const Spacer = styled.span`
  flex: 1 1 auto;
`;

export const ThreadsConnectPanel: React.FC<{ communityId?: string }> = ({
  communityId,
}) => {
  const [config, setConfig] = useState<ThreadsConnection | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!communityId) return;
    setLoading(true);
    setError("");
    try {
      setConfig(await getThreadsConnection(communityId));
    } catch (e: any) {
      setError(e?.message || "Could not load Threads status.");
    } finally {
      setLoading(false);
    }
  }, [communityId]);

  useEffect(() => {
    refresh();
  }, [refresh]);
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const connect = async () => {
    if (!communityId) return;
    setBusy(true);
    setError("");
    try {
      const { url } = await startThreadsAuth(communityId);
      // Redirect in the current tab. Opening a new tab after awaiting the
      // callable can be blocked because the original click activation has
      // expired by the time the OAuth URL is returned.
      window.location.assign(url);
    } catch (e: any) {
      setError(e?.message || "Could not start the Threads connection.");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!communityId || !window.confirm("Disconnect this Threads profile?")) return;
    setBusy(true);
    setError("");
    try {
      await disconnectThreads(communityId);
      await refresh();
    } catch (e: any) {
      setError(e?.message || "Could not disconnect Threads.");
    } finally {
      setBusy(false);
    }
  };

  if (!communityId) return null;
  const connected = Boolean(config?.connected);

  return (
    <PanelRoot>
      <Label>Threads</Label>
      {loading && !config ? (
        <Muted>Checking…</Muted>
      ) : connected ? (
        <Chip>
          {config?.username ? `@${config.username}` : "Connected"}
        </Chip>
      ) : (
        <Muted>No Threads profile connected yet.</Muted>
      )}
      <Spacer />
      {config?.stale ? (
        <Muted style={{ color: "var(--color-danger)" }}>
          {config.staleReason || "Reconnect required."}
        </Muted>
      ) : null}
      {error ? <Muted style={{ color: "var(--color-danger)" }}>{error}</Muted> : null}
      {connected ? (
        <Button type="button" onClick={disconnect} disabled={busy}>
          Disconnect
        </Button>
      ) : (
        <PrimaryButton type="button" onClick={connect} disabled={busy}>
          {busy ? "Opening…" : "Connect Threads"}
        </PrimaryButton>
      )}
    </PanelRoot>
  );
};

export default ThreadsConnectPanel;
